/**
 * Dedicated auth for the superadmin backoffice — a SEPARATE product from the
 * tenant app, so it must NOT share the contractació session.
 *
 * Separation & reliability:
 *  - Own MSAL PublicClientApplication with `cacheLocation: "sessionStorage"`,
 *    isolated from the tenant app's `localStorage`. Signing into contractació
 *    never signs you into the superadmin and vice-versa.
 *  - Login is a REDIRECT with `prompt: "select_account"` to the already-registered
 *    origin (no new redirect URI needed). You always get the account picker
 *    (incl. "Use another account"), never a silently-reused session.
 *  - The redirect lands on the origin; `completeSuperadminRedirect()` is invoked
 *    from main.tsx BEFORE the tenant provider mounts (gated by a sessionStorage
 *    flag set only for superadmin logins), so the two MSAL instances never race
 *    to consume each other's response. On success it bounces to /superadmin.
 *  - Logout is local-only (purge this surface's sessionStorage): forgets the
 *    superadmin session without touching contractació.
 */
import { PublicClientApplication, InteractionRequiredAuthError, type AccountInfo } from "@azure/msal-browser"
import { useCallback, useEffect, useState } from "react"

export const SUPERADMIN_PENDING_KEY = "superadmin.login.pending"
const SUPERADMIN_PATH = "/superadmin"

const rc = (window as unknown as { __CONTRACTACIO_CONFIG__?: Record<string, string> }).__CONTRACTACIO_CONFIG__ ?? {}
const clientId = rc.msalClientId ?? (import.meta.env.VITE_ENTRA_CLIENT_ID as string) ?? ""
const authority =
	rc.msalAuthority ??
	(import.meta.env.VITE_AUTHORITY as string) ??
	`https://login.microsoftonline.com/${rc.msalTenantId ?? "organizations"}`
const knownAuthorities = (rc.msalKnownAuthorities ?? (import.meta.env.VITE_KNOWN_AUTHORITIES as string) ?? "")
	.split(",")
	.filter(Boolean)
const apiScope = rc.apiScope ?? (import.meta.env.VITE_API_SCOPE as string) ?? (clientId ? `api://${clientId}/access` : "")

const API_SCOPES = [apiScope].filter(Boolean)
const LOGIN_SCOPES = ["openid", "profile", "email", ...API_SCOPES]
// The origin is an already-registered SPA redirect URI (same as the tenant app).
const REDIRECT_URI = window.location.origin

let pca: PublicClientApplication | null = null
let initPromise: Promise<void> | null = null

function instance(): PublicClientApplication {
	if (!pca) {
		let hosts = knownAuthorities
		if (hosts.length === 0) {
			try {
				hosts = [new URL(authority).hostname]
			} catch {
				hosts = []
			}
		}
		pca = new PublicClientApplication({
			auth: { clientId, authority, knownAuthorities: hosts, redirectUri: REDIRECT_URI },
			// sessionStorage → isolated from the tenant app (which uses localStorage).
			cache: { cacheLocation: "sessionStorage" },
		})
		initPromise = pca.initialize()
	}
	return pca
}

function purgeLocalSession() {
	for (const key of Object.keys(window.sessionStorage)) {
		if (key.startsWith("msal.") || (clientId && key.includes(clientId)) || key.includes("ciamlogin") || key.includes("login.microsoftonline")) {
			window.sessionStorage.removeItem(key)
		}
	}
}

/**
 * Called once from main.tsx (only when the pending flag is set) to finish a
 * superadmin redirect login before the tenant provider mounts. Returns the
 * signed-in account, or null if there was nothing to complete.
 */
export async function completeSuperadminRedirect(): Promise<AccountInfo | null> {
	const p = instance()
	await initPromise
	const result = await p.handleRedirectPromise()
	const account = result?.account ?? p.getActiveAccount() ?? p.getAllAccounts()[0] ?? null
	if (account) p.setActiveAccount(account)
	return account
}

export interface SuperadminAuth {
	isLoading: boolean
	isAuthenticated: boolean
	account: AccountInfo | null
	login: () => Promise<void>
	logout: () => Promise<void>
	getAccessToken: () => Promise<string>
}

// Dev-only bypass: LOCAL_MODE has no MSAL/CIAM, so fake an authenticated
// superadmin to preview the UI locally (the API is mocked in local dev).
const LOCAL_DEV = import.meta.env.VITE_LOCAL_MODE === "true"
const LOCAL_ACCOUNT = { username: "local@optimtech.es", name: "Local Dev" } as unknown as AccountInfo

export function useSuperadminAuth(): SuperadminAuth {
	const [isLoading, setIsLoading] = useState(true)
	const [account, setAccount] = useState<AccountInfo | null>(null)

	useEffect(() => {
		let alive = true
		;(async () => {
			const p = instance()
			await initPromise
			const acc = p.getActiveAccount() ?? p.getAllAccounts()[0] ?? null
			if (acc) p.setActiveAccount(acc)
			if (alive) {
				setAccount(acc)
				setIsLoading(false)
			}
		})().catch(() => {
			if (alive) {
				setAccount(null)
				setIsLoading(false)
			}
		})
		return () => {
			alive = false
		}
	}, [])

	const login = useCallback(async () => {
		const p = instance()
		await initPromise
		// Flag so main.tsx completes THIS instance's redirect (and not the tenant's).
		window.sessionStorage.setItem(SUPERADMIN_PENDING_KEY, "1")
		await p.loginRedirect({ scopes: LOGIN_SCOPES, prompt: "select_account", redirectUri: REDIRECT_URI })
	}, [])

	const logout = useCallback(async () => {
		// Local-only logout: forget this surface's session without touching the
		// tenant app or bouncing through the CIAM sign-out picker.
		purgeLocalSession()
		try {
			instance().setActiveAccount(null)
		} catch {
			/* noop */
		}
		setAccount(null)
	}, [])

	const getAccessToken = useCallback(async (): Promise<string> => {
		const p = instance()
		await initPromise
		const acc = p.getActiveAccount() ?? p.getAllAccounts()[0]
		if (!acc) throw new Error("[superadmin-auth] no authenticated account")
		try {
			const r = await p.acquireTokenSilent({ scopes: API_SCOPES, account: acc })
			return r.accessToken
		} catch (err) {
			if (err instanceof InteractionRequiredAuthError) {
				window.sessionStorage.setItem(SUPERADMIN_PENDING_KEY, "1")
				await p.acquireTokenRedirect({ scopes: API_SCOPES, account: acc, redirectUri: REDIRECT_URI })
				return ""
			}
			throw err
		}
	}, [])

	if (LOCAL_DEV) {
		return {
			isLoading: false,
			isAuthenticated: true,
			account: LOCAL_ACCOUNT,
			login: async () => {},
			logout: async () => {},
			getAccessToken: async () => "local-dev",
		}
	}
	return { isLoading, isAuthenticated: !!account, account, login, logout, getAccessToken }
}

export { SUPERADMIN_PATH }
